import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { SkillTriggerKindSchema } from '../admin/agent-skill-definitions.schemas';

/**
 * The status the Skills area renders. DERIVED from the instance row, never
 * stored — `revoked_at`, `paused_reason` and `enabled` already determine it, and
 * a stored copy would be a second source of truth free to disagree with the
 * first. German labels live in `@immo/i18n`.
 */
export const SKILL_INSTANCE_STATUSES = ['REVOKED', 'PAUSED_OPERATOR', 'PAUSED_FAILURES', 'INACTIVE', 'ACTIVE'] as const;

export const SkillInstanceStatusSchema = z.enum(SKILL_INSTANCE_STATUSES);

export type SkillInstanceStatus = (typeof SKILL_INSTANCE_STATUSES)[number];

/** What a run does about a schedule slot it missed while the instance was down. */
export const MISSED_RUN_POLICIES = ['SKIP', 'CATCH_UP_ONCE'] as const;

export const MissedRunPolicySchema = z.enum(MISSED_RUN_POLICIES);

export type MissedRunPolicy = (typeof MISSED_RUN_POLICIES)[number];

/** The shortest recurrence a schedule may declare (IMM-436.7 evaluates it). */
export const MIN_SCHEDULE_INTERVAL_MINUTES = 15;
export const DEFAULT_COALESCING_WINDOW_MINUTES = 5;

/**
 * A structured recurrence rather than a cron string: `times` are `HH:MM` in the
 * instance's own timezone, `weekdays` are ISO-8601 (1 = Monday). Operators and
 * members read and write this in a form, so it must be inspectable field by
 * field, which a cron expression is not.
 */
export const SkillScheduleSchema = z.object({
  times: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)),
  weekdays: z.array(z.number().int().min(1).max(7)),
});

export type SkillSchedule = z.infer<typeof SkillScheduleSchema>;

/**
 * The trigger half of an instance's configuration. Kept as one object because
 * the fields are only meaningful together — `eventSubscriptions` is required
 * exactly when the kind is `EVENT`, `schedule` exactly when it is `SCHEDULE`,
 * and the domain validator is the authority on that pairing.
 */
export const SkillTriggerConfigSchema = z.object({
  kind: SkillTriggerKindSchema,
  eventSubscriptions: z.array(z.string()).nullable(),
  coalescingWindowMinutes: z.number().int().nullable(),
  schedule: SkillScheduleSchema.nullable(),
  timezone: z.string().min(1),
  missedRunPolicy: MissedRunPolicySchema,
});

export type SkillTriggerConfig = z.infer<typeof SkillTriggerConfigSchema>;

/**
 * One event an EVENT-triggered instance may subscribe to (IMM-436.6).
 *
 * `eventName` is the wire name the outbox delivers and the instance stores;
 * `module` is the branch of the contract's event tree it came from, which is all
 * the grouping the picker needs. There is deliberately no label here: the
 * catalogue is DERIVED from the contract tree, so any hand-written label set
 * would drift silently the moment a module publishes a new event — the picker
 * would show it untranslated, and nobody adding the event would be looking at
 * an i18n file.
 */
export const SkillEventCatalogueEntrySchema = z.object({
  eventName: z.string().min(1),
  module: z.string().min(1),
});

export type SkillEventCatalogueEntry = z.infer<typeof SkillEventCatalogueEntrySchema>;

export const ListSkillEventCatalogueResponseSchema = z.object({
  data: z.array(SkillEventCatalogueEntrySchema),
});

export type ListSkillEventCatalogueResponse = z.infer<typeof ListSkillEventCatalogueResponseSchema>;

/** One tool on the consent screen, labelled by what it may do without asking. */
export const SkillToolAnnotationSchema = z.object({
  toolId: z.string().min(1),
  // Interim: until IMM-436.4's catalogue carries a side-effect class, every
  // catalogued tool is a read. `null` means the tool id is no longer catalogued.
  sideEffect: z.enum(['QUERY', 'AUTO_WITH_AUDIT', 'APPROVAL_REQUIRED']).nullable(),
});

export type SkillToolAnnotation = z.infer<typeof SkillToolAnnotationSchema>;

/** One card in the member's Skills area. */
export const SkillInstanceSummarySchema = z.object({
  id: z.uuid(),
  definitionId: z.uuid(),
  definitionKey: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  status: SkillInstanceStatusSchema,
  triggerKind: SkillTriggerKindSchema,
  grantingMemberId: refersTo('member', z.uuid()),
  grantedAt: z.iso.datetime().nullable(),
  nextRunAt: z.iso.datetime().nullable(),
});

export type SkillInstanceSummary = z.infer<typeof SkillInstanceSummarySchema>;

/** A definition a member may still activate — no instance of it exists yet. */
export const AvailableSkillSchema = z.object({
  definitionId: z.uuid(),
  definitionKey: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  supportedTriggers: z.array(SkillTriggerKindSchema),
  defaultAllowlist: z.array(z.string()),
  // The same annotations the detail view carries, because the CONSENT step is
  // where they matter most: a member is told which of a skill's tools merely
  // read and which act on their behalf BEFORE they grant it, not after. Without
  // these the consent screen could only guess, and it guessed „without asking"
  // for everything — which stopped being true the moment a tool needed approval.
  tools: z.array(SkillToolAnnotationSchema),
  approvalWindowDays: z.number().int(),
});

export type AvailableSkill = z.infer<typeof AvailableSkillSchema>;

export const ListSkillInstancesResponseSchema = z.object({
  data: z.array(SkillInstanceSummarySchema),
  available: z.array(AvailableSkillSchema),
});

export type ListSkillInstancesResponse = z.infer<typeof ListSkillInstancesResponseSchema>;

/**
 * The detail view behind the consent step and the configure panel: the stored
 * configuration, the grant state, and the definition's tools annotated by what
 * each may do without asking.
 */
export const SkillInstanceDetailSchema = SkillInstanceSummarySchema.extend({
  toolAllowlist: z.array(z.string()),
  triggerConfig: SkillTriggerConfigSchema,
  settings: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  revokedAt: z.iso.datetime().nullable(),
  definitionAllowlist: z.array(z.string()),
  supportedTriggers: z.array(SkillTriggerKindSchema),
  approvalWindowDays: z.number().int(),
  tools: z.array(SkillToolAnnotationSchema),
});

export type SkillInstanceDetail = z.infer<typeof SkillInstanceDetailSchema>;

export const SkillInstancePathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const SkillInstanceWorkspaceParamsSchema = z.object({
  workspaceId: z.uuid(),
});

/**
 * Deliberately permissive on the configuration: every pairing rule is enforced
 * by the domain validator so a refusal carries a stable
 * `assistant.skill-instance.*` code and a reason, rather than a generic zod 400.
 */
export const CreateSkillInstanceBodySchema = z.object({
  definitionId: z.uuid(),
  // No `grantingMemberId`: an instance always belongs to the caller. It is the
  // server that binds it, from the authenticated workspace member — the client
  // cannot even name a member, since `/auth/me` carries user and organisation
  // ids but no member id. Accepting one here only ever produced instances that
  // `grant`/`list`/`run` — all of which resolve the owner from the actor —
  // could never find again.
  toolAllowlist: z.array(z.string()),
  triggerConfig: SkillTriggerConfigSchema,
  settings: z.record(z.string(), z.unknown()),
});

export type CreateSkillInstanceBody = z.infer<typeof CreateSkillInstanceBodySchema>;

export const CreateSkillInstanceResponseSchema = z.object({ id: z.uuid() });

export type CreateSkillInstanceResponse = z.infer<typeof CreateSkillInstanceResponseSchema>;

export const UpdateSkillInstanceConfigBodySchema = z.object({
  toolAllowlist: z.array(z.string()),
  triggerConfig: SkillTriggerConfigSchema,
  settings: z.record(z.string(), z.unknown()),
  expectedVersion: z.number().int(),
});

export type UpdateSkillInstanceConfigBody = z.infer<typeof UpdateSkillInstanceConfigBodySchema>;

/** Grant and revoke carry no fields — the actor and the instance are the input. */
export const SkillInstanceActionBodySchema = z.object({}).strict();
